/**
 * Ticket List Component
 *
 * Displays a list of the user's support tickets.
 */

import { format } from "date-fns";
import { MessageSquare, ChevronRight, Inbox } from "lucide-react";
import type { SupportTicketSummary } from "../../types/support";
import { STATUS_LABELS, CATEGORY_LABELS } from "../../types/support";

interface TicketListProps {
  tickets: SupportTicketSummary[];
  onSelect: (ticketId: string) => void;
}

export function TicketList({ tickets, onSelect }: TicketListProps) {
  if (tickets.length === 0) {
    return (
      <div className="text-center py-12">
        <Inbox className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
        <p className="text-muted-foreground">No support tickets yet</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-border">
      {tickets.map((ticket) => (
        <button
          key={ticket.id}
          onClick={() => onSelect(ticket.id)}
          className="w-full text-left p-4 hover:bg-muted/50 transition-colors flex items-center gap-4"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-muted-foreground font-mono">
                {ticket.ticket_number}
              </span>
              <StatusBadge status={ticket.status} />
            </div>
            <h3 className="font-medium text-foreground truncate">
              {ticket.subject}
            </h3>
            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
              <span>{CATEGORY_LABELS[ticket.category]}</span>
              <span className="flex items-center gap-1">
                <MessageSquare className="w-3 h-3" />
                {ticket.message_count}
              </span>
              <span>
                {format(new Date(ticket.updated_at), "MMM d, h:mm a")}
              </span>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        </button>
      ))}
    </div>
  );
}

function StatusBadge({ status }: { status: SupportTicketSummary["status"] }) {
  const colors: Record<SupportTicketSummary["status"], string> = {
    open: "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200",
    in_progress:
      "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200",
    waiting_on_customer:
      "bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200",
    resolved:
      "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200",
    closed: "bg-muted text-muted-foreground",
  };

  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}
